import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { User, Bell, Lock, Save, ShieldAlert } from "lucide-react";

export default function MonCompte() {
    const { user, refreshMe: refreshUser } = useAuth();
    return (
        <section className="max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="mon-compte-page">
            <p className="bc-label-small">Espace Pro</p>
            <h1 className="font-serif text-[#D4BC7C] text-3xl md:text-4xl mt-4">Mon compte</h1>
            <div className="bc-hairline-gold my-6" />
            <p className="text-sm text-[#F2ECE0]/80">
                Bienvenue {user?.prenom || "cher partenaire"}. Gérez vos informations, vos préférences email et votre mot de passe.
            </p>

            <div className="mt-10 space-y-10">
                <ProfileSection user={user} onSaved={refreshUser} />
                <PreferencesSection />
                <PasswordSection />
            </div>
        </section>
    );
}

/* ---------------------------------------------------------------- */
/* Profile section                                                  */
/* ---------------------------------------------------------------- */
function ProfileSection({ user, onSaved }) {
    const [form, setForm] = useState({
        prenom: "",
        nom: "",
        raison_sociale: "",
        fonction: "",
        telephone: "",
        ide_tva: "",
    });
    const [initialIde, setInitialIde] = useState("");

    useEffect(() => {
        if (user) {
            setForm({
                prenom: user.prenom || "",
                nom: user.nom || "",
                raison_sociale: user.raison_sociale || "",
                fonction: user.fonction || "",
                telephone: user.telephone || "",
                ide_tva: user.ide_tva || "",
            });
            setInitialIde(user.ide_tva || "");
        }
    }, [user]);

    const save = useMutation({
        mutationFn: (payload) => api.patch("/me/profile", payload),
        onSuccess: () => {
            toast.success("Vos informations ont été mises à jour.");
            onSaved?.();
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Impossible d'enregistrer."),
    });

    const ideChanged = form.ide_tva.trim() !== (initialIde || "").trim();

    return (
        <div className="bc-card" data-testid="profile-section">
            <div className="flex items-center gap-3 mb-6">
                <User size={18} className="text-[#D4BC7C]" />
                <h2 className="font-serif text-[#D4BC7C] text-xl">Mes informations</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="bc-label-small">Prénom</label>
                    <input
                        className="bc-input"
                        value={form.prenom}
                        onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                        data-testid="profile-prenom"
                    />
                </div>
                <div>
                    <label className="bc-label-small">Nom</label>
                    <input
                        className="bc-input"
                        value={form.nom}
                        onChange={(e) => setForm({ ...form, nom: e.target.value })}
                        data-testid="profile-nom"
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="bc-label-small">Raison sociale</label>
                    <input
                        className="bc-input"
                        value={form.raison_sociale}
                        onChange={(e) => setForm({ ...form, raison_sociale: e.target.value })}
                        data-testid="profile-raison-sociale"
                    />
                </div>
                <div>
                    <label className="bc-label-small">Fonction</label>
                    <input
                        className="bc-input"
                        value={form.fonction}
                        onChange={(e) => setForm({ ...form, fonction: e.target.value })}
                        data-testid="profile-fonction"
                    />
                </div>
                <div>
                    <label className="bc-label-small">Téléphone</label>
                    <input
                        className="bc-input"
                        value={form.telephone}
                        onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                        data-testid="profile-telephone"
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="bc-label-small flex items-center gap-2">
                        Numéro IDE / TVA
                        <ShieldAlert size={12} className="text-[#D4BC7C]" />
                    </label>
                    <input
                        className="bc-input"
                        value={form.ide_tva}
                        onChange={(e) => setForm({ ...form, ide_tva: e.target.value })}
                        data-testid="profile-ide-tva"
                        placeholder="CHE-123.456.789"
                    />
                    {ideChanged && (
                        <div
                            className="mt-2 p-3 flex items-start gap-2.5"
                            style={{
                                background: "rgba(229,208,150,0.08)",
                                border: "1px solid rgba(229,208,150,0.4)",
                                borderLeft: "3px solid #e5d096",
                            }}
                            data-testid="profile-ide-warning"
                        >
                            <ShieldAlert size={16} className="mt-0.5 flex-shrink-0" color="#e5d096" strokeWidth={2.2} />
                            <p className="text-xs text-[#F2ECE0]/90">
                                <strong className="text-[#e5d096]">Champ sensible :</strong> toute modification de l&apos;IDE/TVA sera signalée automatiquement à l&apos;équipe Boucherie Costa pour vérification.
                            </p>
                        </div>
                    )}
                </div>
                <div className="md:col-span-2">
                    <label className="bc-label-small">E-mail (non modifiable)</label>
                    <input
                        className="bc-input"
                        value={user?.email || ""}
                        disabled
                        data-testid="profile-email"
                        style={{ opacity: 0.6 }}
                    />
                </div>
            </div>

            <div className="mt-6 flex justify-end">
                <button
                    className="bc-btn"
                    onClick={() => save.mutate(form)}
                    disabled={save.isPending}
                    data-testid="profile-save"
                >
                    <Save size={14} /> {save.isPending ? "Enregistrement…" : "Enregistrer"}
                </button>
            </div>
        </div>
    );
}

/* ---------------------------------------------------------------- */
/* Preferences section (authenticated)                              */
/* ---------------------------------------------------------------- */
const DEFAULT_PREFS = {
    email_new_suggestions: true,
    email_announcements: true,
    email_order_confirmed: true,
    email_order_en_traitement: false,
    email_order_en_production: false,
    email_order_en_livraison: true,
    email_order_livree: false,
};

function PreferencesSection() {
    const qc = useQueryClient();
    const { data, isLoading } = useQuery({
        queryKey: ["my-preferences"],
        queryFn: async () => (await api.get("/me/preferences")).data,
    });
    const [prefs, setPrefs] = useState(DEFAULT_PREFS);

    useEffect(() => {
        if (data) setPrefs({ ...DEFAULT_PREFS, ...data });
    }, [data]);

    const save = useMutation({
        mutationFn: (payload) => api.patch("/me/preferences", payload),
        onSuccess: () => {
            toast.success("Préférences enregistrées.");
            qc.invalidateQueries({ queryKey: ["my-preferences"] });
        },
        onError: () => toast.error("Impossible d'enregistrer les préférences."),
    });

    return (
        <div className="bc-card" data-testid="preferences-section">
            <div className="flex items-center gap-3 mb-6">
                <Bell size={18} className="text-[#D4BC7C]" />
                <h2 className="font-serif text-[#D4BC7C] text-xl">Mes préférences email</h2>
            </div>

            {isLoading ? (
                <p className="text-sm text-[#A0A0A0]">Chargement…</p>
            ) : (
                <div className="space-y-5">
                    <p className="bc-label-small">Contenus éditoriaux</p>
                    <ToggleRow label="Nouvelles suggestions" hint="Suggestions du moment publiées par Leonel."
                        value={prefs.email_new_suggestions} onChange={(v) => setPrefs({ ...prefs, email_new_suggestions: v })}
                        testId="mc-pref-suggestions" />
                    <ToggleRow label="Informations importantes" hint="Fermetures, vacances, changements d&apos;horaires."
                        value={prefs.email_announcements} onChange={(v) => setPrefs({ ...prefs, email_announcements: v })}
                        testId="mc-pref-announcements" />

                    <div className="bc-hairline" />
                    <p className="bc-label-small">Statut de mes commandes</p>
                    <ToggleRow label="Commande confirmée (30 min après validation)" hint="« Leonel prépare votre commande » — recommandé."
                        value={prefs.email_order_confirmed} onChange={(v) => setPrefs({ ...prefs, email_order_confirmed: v })}
                        testId="mc-pref-order-confirmed" />
                    <ToggleRow label="En traitement" hint="Statut initial à la validation."
                        value={prefs.email_order_en_traitement} onChange={(v) => setPrefs({ ...prefs, email_order_en_traitement: v })}
                        testId="mc-pref-order-en_traitement" />
                    <ToggleRow label="En production" hint="Articles en préparation."
                        value={prefs.email_order_en_production} onChange={(v) => setPrefs({ ...prefs, email_order_en_production: v })}
                        testId="mc-pref-order-en_production" />
                    <ToggleRow label="En cours de livraison" hint="Commande prête, en route — recommandé."
                        value={prefs.email_order_en_livraison} onChange={(v) => setPrefs({ ...prefs, email_order_en_livraison: v })}
                        testId="mc-pref-order-en_livraison" />
                    <ToggleRow label="Livrée" hint="Notification à la réception."
                        value={prefs.email_order_livree} onChange={(v) => setPrefs({ ...prefs, email_order_livree: v })}
                        testId="mc-pref-order-livree" />

                    <div className="pt-2 flex justify-end">
                        <button
                            className="bc-btn"
                            onClick={() => save.mutate(prefs)}
                            disabled={save.isPending}
                            data-testid="mc-pref-save"
                        >
                            <Save size={14} /> Enregistrer
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---------------------------------------------------------------- */
/* Password section                                                 */
/* ---------------------------------------------------------------- */
function PasswordSection() {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");

    const change = useMutation({
        mutationFn: (payload) => api.post("/me/password", payload),
        onSuccess: (res) => {
            toast.success("Mot de passe modifié.");
            // Rotate the client-side token so the current session keeps working after the JWT rotation
            if (res?.data?.access_token) {
                try {
                    localStorage.setItem("bc_access_token", res.data.access_token);
                } catch {
                    /* ignore */
                }
            }
            setCurrent(""); setNext(""); setConfirm("");
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Impossible de modifier le mot de passe."),
    });

    const submit = (e) => {
        e.preventDefault();
        if (next.length < 8) return toast.error("Le nouveau mot de passe doit faire au moins 8 caractères.");
        if (next !== confirm) return toast.error("La confirmation ne correspond pas.");
        change.mutate({ current_password: current, new_password: next });
    };

    return (
        <div className="bc-card" data-testid="password-section">
            <div className="flex items-center gap-3 mb-6">
                <Lock size={18} className="text-[#D4BC7C]" />
                <h2 className="font-serif text-[#D4BC7C] text-xl">Mot de passe</h2>
            </div>
            <form onSubmit={submit} className="grid md:grid-cols-3 gap-4 items-end">
                <div>
                    <label className="bc-label-small">Actuel</label>
                    <input type="password" className="bc-input" value={current} onChange={(e) => setCurrent(e.target.value)}
                        autoComplete="current-password" data-testid="pwd-current" required />
                </div>
                <div>
                    <label className="bc-label-small">Nouveau (min. 8 caractères)</label>
                    <input type="password" className="bc-input" value={next} onChange={(e) => setNext(e.target.value)}
                        autoComplete="new-password" data-testid="pwd-new" minLength={8} required />
                </div>
                <div>
                    <label className="bc-label-small">Confirmation</label>
                    <input type="password" className="bc-input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                        autoComplete="new-password" data-testid="pwd-confirm" minLength={8} required />
                </div>
                <div className="md:col-span-3 flex justify-end">
                    <button type="submit" className="bc-btn" disabled={change.isPending} data-testid="pwd-save">
                        <Save size={14} /> {change.isPending ? "Modification…" : "Modifier le mot de passe"}
                    </button>
                </div>
            </form>
        </div>
    );
}

/* ---------------------------------------------------------------- */
/* Reusable toggle row                                              */
/* ---------------------------------------------------------------- */
function ToggleRow({ label, hint, value, onChange, testId }) {
    return (
        <label className="flex items-start justify-between gap-4 cursor-pointer">
            <div className="flex-1">
                <p className="text-[#F2ECE0] text-sm font-medium">{label}</p>
                <p className="text-xs text-[#A0A0A0] mt-1">{hint}</p>
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={value}
                onClick={() => onChange(!value)}
                data-testid={testId}
                style={{
                    width: "52px",
                    height: "28px",
                    background: value ? "#D4BC7C" : "rgba(212,188,124,0.15)",
                    border: "1px solid #D4BC7C",
                    position: "relative",
                    cursor: "pointer",
                    transition: "background-color 0.25s ease",
                    flexShrink: 0,
                }}
            >
                <span
                    style={{
                        position: "absolute",
                        top: "2px",
                        left: value ? "26px" : "3px",
                        width: "22px",
                        height: "22px",
                        background: value ? "#000" : "#D4BC7C",
                        transition: "left 0.25s cubic-bezier(0.4,0,0.2,1)",
                    }}
                />
            </button>
        </label>
    );
}
